/**
 * Guard Vue 3's DOM patching against in-browser page translation.
 *
 * Google / Chrome / iOS "Translate this page" rewrites text nodes in place,
 * wrapping them in <font> tags and reparenting them. Vue's virtual-DOM still
 * holds the pre-translation vnode tree, so a later patch calls removeChild /
 * insertBefore with a reference node whose parent has changed. Depending on the
 * tree shape this either throws a NotFoundError or sends the runtime's
 * patch/unmount pair into mutual recursion until the stack overflows
 * ("RangeError: Maximum call stack size exceeded").
 *
 * These wrappers keep removeChild / insertBefore from throwing or recursing when
 * the node is no longer where Vue thinks it is, while still doing the real DOM
 * work (removing the node from its actual parent / keeping the inserted node on
 * the page). Browser translation keeps working; Vue stops crashing.
 *
 * See eaulc-web-app#463. Must run before createApp()/mount() so the patched
 * prototypes are in place for the first render.
 */
export function installTranslateDomGuard() {
  if (typeof Node !== 'function' || !Node.prototype) return;
  // Idempotent — avoid double-wrapping on HMR / repeated imports.
  if (Node.prototype.__translateDomGuardInstalled) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild(child) {
    if (child && child.parentNode !== this) {
      // The child was reparented out from under Vue (e.g. by in-browser page
      // translation). Detach it from where it actually lives rather than
      // throwing NotFoundError — still a real removal, so nothing leaks and
      // genuine same-parent misuse elsewhere continues to throw as before.
      if (child.parentNode) {
        return originalRemoveChild.call(child.parentNode, child);
      }
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      // Reference node was reparented by translation; append newNode instead of
      // dropping it, so its rendered content stays on the page.
      return originalInsertBefore.call(this, newNode, null);
    }
    return originalInsertBefore.apply(this, arguments);
  };

  // Non-enumerable so the marker never leaks into for...in / serialization,
  // but still writable+configurable so tests can reset it.
  Object.defineProperty(Node.prototype, '__translateDomGuardInstalled', {
    value: true,
    writable: true,
    configurable: true
  });
}
